import React, { ReactNode } from "react";
import styled, { css, useTheme } from "styled-components";
import { BacktestingRunType, BotType } from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import IconWithFallback from "Global/IconWithFallback";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import {
  formatSignedPercent,
  getBacktestSubject,
} from "features/bots/BotForm/Backtesting/NewLook/format";
import useNetPnl from "features/bots/BotForm/Backtesting/NewLook/useNetPnl";
import useBacktestMoney from "features/bots/BotForm/Backtesting/NewLook/useBacktestMoney";

/**
 * Cells of the new-look backtests list, in the dashboard's roles: a name
 * over a "what · where" meta line, figures (15/500, tabular) over a quiet
 * meta line, colour only for the PnL's direction.
 */

interface BacktestIconProps {
  run?: BacktestingRunType;
  botType: BotType;
  size?: number;
}

/** The group's picture for signals bots, the coin for DCA bots. */
export const BacktestIcon = ({ run, botType, size = 28 }: BacktestIconProps) =>
  botType === BotType.SIGNAL ? (
    <IconWithFallback
      iconLink={run?.sub_group_image}
      coinOrExchangeName={run?.sub_group_name}
      pxIconSize={size}
      iconStyle={{ borderRadius: 8, marginRight: 0, flex: "none" }}
    />
  ) : (
    <IconWithFallback
      iconType={ICON_TYPES.COIN}
      coinOrExchangeName={run?.symbol}
      pxIconSize={size}
      iconStyle={{ marginRight: 0, flex: "none" }}
    />
  );

interface NameCellProps {
  run: BacktestingRunType;
  botType: BotType;
}

export const NameCell = ({ run, botType }: NameCellProps) => {
  const meta = [getBacktestSubject(run, botType), run.client_name].filter(
    Boolean
  );
  return (
    <Pair>
      <BacktestIcon run={run} botType={botType} />
      <Lines>
        <Name title={run.name}>{run.name}</Name>
        <Meta title={meta.join(" · ")}>{meta.join(" · ")}</Meta>
      </Lines>
    </Pair>
  );
};

interface TextCellProps {
  value: ReactNode;
  meta?: ReactNode;
  end?: boolean;
}

/** A value in the body role over a meta line. */
export const TextCell = ({ value, meta, end }: TextCellProps) => (
  <Lines $end={end}>
    <Value>{value}</Value>
    {meta !== undefined && <Meta>{meta}</Meta>}
  </Lines>
);

export const NetPnlCell = ({ run }: { run: BacktestingRunType }) => {
  const tokens = getNewLookTokens(useTheme());
  const { signedMoney } = useBacktestMoney();
  const { value, percent } = useNetPnl(run);
  return (
    <Lines $end={true}>
      <Figure style={{ color: directionColor(tokens, value) }}>
        {signedMoney(value)}
      </Figure>
      <Meta style={{ color: directionColor(tokens, percent) }} $strong={true}>
        {formatSignedPercent(percent)}
      </Meta>
    </Lines>
  );
};

interface SkeletonCellProps {
  width: number;
  /** A round placeholder for the icon before the lines. */
  icon?: boolean;
  /** One line (status) instead of a value over a meta line. */
  single?: boolean;
  end?: boolean;
}

/** A loading placeholder shaped like a cell (value over meta). */
export const SkeletonCell = ({
  width,
  icon,
  single,
  end,
}: SkeletonCellProps) => {
  const lines = (
    <Lines $end={end} style={{ gap: 6 }}>
      <Skeleton width={width} height={12} />
      {!single && <Skeleton width={Math.round(width * 0.6)} height={10} />}
    </Lines>
  );
  if (!icon) return lines;
  return (
    <Pair>
      <Skeleton width={28} height={28} radius={14} />
      {lines}
    </Pair>
  );
};

const Pair = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
const Lines = styled.div<{ $end?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  ${(p) =>
    p.$end &&
    css`
      align-items: flex-end;
      text-align: right;
    `}
`;
const Name = styled.span`
  display: block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Value = styled.span`
  ${newLookType.body};
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: nowrap;
`;
const Figure = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Meta = styled.span<{ $strong?: boolean }>`
  display: block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  ${newLookType.meta};
  ${(p) => p.$strong && "font-weight: 500;"}
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
